import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { SegmentQuoteItem } from '../api/client';

// ─────────────────────────────────────────────────────────────────────────────
// Market-Research Insights section on the segment profile page. Renders the
// quotes that were extracted at segment-save time by the hackathon
// quote-extraction pipeline (see useSaveSegmentPages.ts).
//
// Data source: segment metadata.quotes = { subject, items: SegmentQuoteItem[] }
// where SegmentQuoteItem comes from FindRelevantQuotesQueryEngine. Each item
// carries transcriptId + startTs/endTs so this widget can link directly to
// the source transcript in brand-insights.
// ─────────────────────────────────────────────────────────────────────────────

export interface SegmentQuotesData {
  subject?: string;
  items?: SegmentQuoteItem[];
  /** Indices of quotes hidden by the user. Persisted on save. */
  hiddenIndices?: number[];
}

interface Props {
  quotes: SegmentQuotesData | null | undefined;
  segmentName?: string;
  /** When true, shows the edit pencil. Controlled by parent. */
  editMode?: boolean;
  /** When provided, enables inline editing (hide/restore quotes). */
  onSave?: (updatedQuotes: SegmentQuotesData) => Promise<void>;
}

export default function SegmentMarketResearchInsights({
  quotes,
  segmentName: _segmentName,
  editMode,
  onSave,
}: Props): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const items = quotes?.items ?? [];
  const [hiddenIndices, setHiddenIndices] = useState<Set<number>>(new Set(quotes?.hiddenIndices ?? []));

  const handleToggleHide = useCallback((idx: number) => {
    setHiddenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave || !quotes) return;
    setSaving(true);
    try {
      await onSave({
        ...quotes,
        hiddenIndices: Array.from(hiddenIndices).sort((a, b) => a - b),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }, [onSave, quotes, hiddenIndices]);

  const handleCancel = useCallback(() => {
    setHiddenIndices(new Set(quotes?.hiddenIndices ?? []));
    setEditing(false);
  }, [quotes]);

  if (!quotes || items.length === 0) return null;

  const visibleItems = editing ? items : items.filter((_, idx) => !hiddenIndices.has(idx));

  if (!editing && visibleItems.length === 0) return null;

  return (
    <Root>
      <Header onClick={() => !editing && setExpanded(!expanded)}>
        <HeaderTitle>
          <span>Market Research Insights</span>
        </HeaderTitle>
        <HeaderActions>
          {onSave && editMode && !editing && (
            <EditBtn
              onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
              }}
              title="Edit quotes"
            >
              <EditIcon fontSize="small" />
            </EditBtn>
          )}
          {editing && (
            <>
              <SaveBtn
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={saving}
                title="Save changes"
              >
                Save
              </SaveBtn>
              <CancelBtn
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                disabled={saving}
                title="Cancel"
              >
                <CloseIcon fontSize="small" />
              </CancelBtn>
            </>
          )}
          {!editing && (
            <ExpandIcon $expanded={expanded}>
              <ExpandMoreIcon />
            </ExpandIcon>
          )}
        </HeaderActions>
      </Header>
      {expanded && (
        <Content>
          {editing && (
            <EditHint>
              Click the eye icon on a quote to hide or restore it.
              {hiddenIndices.size > 0 && ` (${hiddenIndices.size} hidden)`}
            </EditHint>
          )}
          <QuoteGrid>
            {(editing ? items : visibleItems).map((q, rawIdx) => {
              const originalIdx = editing ? rawIdx : items.indexOf(q);
              const isHidden = hiddenIndices.has(originalIdx);
              return (
                <QuoteCard
                  key={`${q.transcriptId}-${q.startTs}-${originalIdx}`}
                  quote={q}
                  hidden={isHidden}
                  editing={editing}
                  onToggleHide={() => handleToggleHide(originalIdx)}
                />
              );
            })}
          </QuoteGrid>
        </Content>
      )}
    </Root>
  );
}

// ─── Single quote card ───────────────────────────────────────────────────────

function QuoteCard({
  quote,
  hidden,
  editing,
  onToggleHide,
}: {
  quote: SegmentQuoteItem;
  hidden?: boolean;
  editing?: boolean;
  onToggleHide?: () => void;
}): React.JSX.Element {
  const link = buildTranscriptLink(quote);
  const timestampLabel = `${formatTime(quote.startTs)} – ${formatTime(quote.endTs)}`;

  return (
    <Card $hidden={hidden && editing}>
      <QuoteIconWrap>
        <FormatQuoteIcon fontSize="small" />
      </QuoteIconWrap>
      <QuoteBody>
        <QuoteText>&ldquo;{quote.value}&rdquo;</QuoteText>
        <QuoteFooter>
          <TranscriptLink
            to={link}
            title={`Open transcript #${quote.transcriptId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Transcript #{quote.transcriptId}
            <LinkChevron fontSize="inherit" />
          </TranscriptLink>
          <Timestamp>{timestampLabel}</Timestamp>
          {quote.starredQuoteId != null && <StarBadge>★ Starred</StarBadge>}
        </QuoteFooter>
      </QuoteBody>
      {editing && (
        <HideButton onClick={onToggleHide} title={hidden ? 'Restore quote' : 'Hide quote'}>
          {hidden ? <RestoreIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
        </HideButton>
      )}
    </Card>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Deep-link into the workspace transcript viewer. The real route is
// /workspaces/transcripts/:id?s=<start seconds> (see
// packages/chat-components/src/utils/navigation.ts → getLocationFor.workspaces.transcript).
// We start the playhead at the quote's startTs so the viewer opens at the
// matching moment.
function buildTranscriptLink(q: SegmentQuoteItem): string {
  const qs = new URLSearchParams({ s: String(Math.floor(q.startTs)) });
  return `/workspaces/transcripts/${q.transcriptId}?${qs.toString()}`;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 12px;
`;

const Header = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 48px;
  box-sizing: border-box;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 18px;
  font-weight: 700;
  color: #323232;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  color: rgba(0, 0, 0, 0.54);
  transition: background 120ms ease;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const SaveBtn = styled.button`
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: #1976d2;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  height: 28px;
  &:hover:not(:disabled) {
    background: #1565c0;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CancelBtn = styled(EditBtn)`
  color: #991b1b;
  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 20px;
`;

const EditHint = styled.div`
  font-size: 12px;
  color: #626262;
  margin-bottom: 8px;
  font-style: italic;
`;

const QuoteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $hidden?: boolean }>`
  display: flex;
  gap: 10px;
  background: ${(p) => (p.$hidden ? '#f5f5f5' : '#fafafa')};
  border: 1px solid ${(p) => (p.$hidden ? '#ddd' : '#e5e5e5')};
  border-left: 3px solid ${(p) => (p.$hidden ? '#bbb' : '#28aae1')};
  border-radius: 6px;
  padding: 12px 14px;
  opacity: ${(p) => (p.$hidden ? 0.5 : 1)};
  transition: opacity 150ms ease;
  position: relative;
`;

const QuoteIconWrap = styled.div`
  color: #28aae1;
  opacity: 0.7;
  flex-shrink: 0;
`;

const QuoteBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const QuoteText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #323232;
  font-style: italic;
`;

const QuoteFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const TranscriptLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  color: #1271c4;
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const LinkChevron = styled(ChevronRightIcon)`
  color: #1271c4;
  font-size: 14px;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #626262;
  font-variant-numeric: tabular-nums;
`;

const StarBadge = styled.span`
  font-size: 10px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 10px;
  padding: 1px 6px;
  font-weight: 600;
`;

const HideButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  &:hover {
    color: rgba(0, 0, 0, 0.7);
  }
`;
